// slider/index.recipe.ts
import {defineSlotRecipe} from '@chakra-ui/react3'

export const sliderSlotRecipe = defineSlotRecipe({
    className: 'slider',
    slots: [
        'sliderContainer',
        'controlsContainer',
        'pagination',
        'slide',
        'scrollContainer',
        'buttonGroup',
        'navButton',
        'cockpitPagination'
    ],

    base: {
        sliderContainer: {
            position: 'relative',
            flexDirection: 'column-reverse',
            overflow: 'visible'
        },
        scrollContainer: {
            overflowX: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': {display: 'none'},
            WebkitOverflowScrolling: 'touch',
            // for full visible focus indicator for (first) visible element
            paddingY: 'spacing-4',
            gap: 'var(--slide-gap, 0px)',
            borderRadius: {s: 'l'}
        },
        controlsContainer: {
            paddingTop: {base: 'spacing-40', s: 'spacing-80'},
            zIndex: 2
        },
        slide: {
            flex: '0 0 auto',
            height: 'auto',
            position: 'relative',
            overflow: 'visible',
            width: 'calc((100% - ( (var(--items-per-slide, 1) - 1) * var(--slide-gap, 0px) )) / var(--items-per-slide, 1))',
            img: {pointerEvents: 'none', userSelect: 'none', draggable: false}
        },
        pagination: {gap: '2px', justifyContent: 'center'},
        cockpitPagination: {display: 'none'},
        buttonGroup: {gap: 'spacing-8'},
        navButton: {
            size: 'md'
        }
    },

    variants: {
        variant: {
            carousel: {
                sliderContainer: {
                    '--items-per-slide': {base: 1, s: 3},
                    '--slide-gap': 'sizes.4'
                },
                controlsContainer: {justifyContent: 'flex-end'},
                scrollContainer: {
                    // using negative margin to span the full visible width
                    // --container-total-inline-padding is computed by parent module-container and includes:
                    // 1. container inline padding (responsive: mobile/desktop)
                    // 2. extra margin from max-width constraint: max(0px, 50cqw - maxWidth * 0.5)
                    // fallback to --container-inline-padding for usage outside module-container
                    marginInline:
                        'calc(-1 * var(--container-total-inline-padding, var(--container-inline-padding, 0px)))',
                    // offset children so they are aligned with the module content
                    paddingInline:
                        'var(--container-total-inline-padding, var(--container-inline-padding, 0px))',
                    scrollPaddingInline:
                        'var(--container-total-inline-padding, var(--container-inline-padding, 0px))'
                },
                pagination: {display: 'none'},
                navButton: {variant: 'secondaryGhost'}
            },
            gallery: {
                slide: {
                    display: 'contents'
                },
                sliderContainer: {
                    '--items-per-slide': 1,
                    '--slide-gap': '0px'
                },
                scrollContainer: {
                    position: 'relative',
                    marginInline: {base: 'calc(-1 * var(--container-inline-padding))', s: 0},
                    display: 'grid',
                    gridAutoFlow: 'column',
                    gridTemplateRows: {base: 'repeat(2, auto)', s: 'auto'},
                    gridAutoColumns: '100%'
                },
                controlsContainer: {
                    flexDirection: {base: 'row-reverse', s: 'revert'},
                    justifyContent: 'space-between',
                    position: 'absolute',
                    left: 'spacing-32',
                    bottom: 'spacing-32',
                    right: {base: 'spacing-32', s: 'auto'},
                    gap: {base: 0, s: 'spacing-32'}
                },
                navButton: {variant: 'mediaGhost'}
            },
            cockpitGallery: {
                sliderContainer: {
                    '--items-per-slide': 1,
                    '--slide-gap': {base: 'sizes.4', s: '0px'},
                    '--cockpit-gallery-padding': 'spacing.container-padding-mobile'
                },
                slide: {
                    background: 'background.section.default',
                    borderRadius: 'm',
                    overflow: 'hidden',
                    width: '100%'
                },

                scrollContainer: {
                    overflowX: 'auto',
                    display: 'flex',
                    flexDirection: 'row',
                    scrollSnapType: 'x mandatory',
                    gap: 'var(--slide-gap)',
                    borderRadius: {s: 'm'},
                    marginInline: {base: 'calc(-1 * var(--cockpit-gallery-padding))', s: 0},
                    paddingInline: {
                        base: 'calc(var(--cockpit-gallery-padding) )',
                        s: 0
                    },

                    scrollPaddingInline: {base: 'var(--cockpit-gallery-padding)', s: 0},
                    paddingY: 'spacing-0'
                },
                controlsContainer: {
                    flexDirection: {base: 'row-reverse', s: 'revert'},
                    justifyContent: 'space-between',
                    position: 'absolute',
                    bottom: 'spacing-16',
                    right: {base: 'spacing-32', s: 'spacing-16'},
                    gap: {base: 0, s: 'spacing-32'}
                },
                pagination: {display: 'none'},
                cockpitPagination: {display: 'flex'},
                buttonGroup: {
                    alignItems: 'center',
                    p: 'spacing-6',
                    borderRadius: 'round',
                    bg: 'primary.base.white',
                    textStyle: 'heading.small'
                },
                navButton: {variant: 'secondaryGhost', size: 'sm'}
            },
            configuratorGallery: {
                sliderContainer: {
                    '--items-per-slide': 1,
                    position: 'absolute',
                    inset: '0'
                },
                scrollContainer: {
                    containerType: 'size',
                    position: 'absolute',
                    inset: '0',
                    paddingY: '0',
                    borderRadius: 'none'
                },
                slide: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& > *': {
                        height: 'min(100cqw, 100cqh)',
                        width: 'min(100cqw, 100cqh)',
                        aspectRatio: '1'
                    }
                },
                controlsContainer: {
                    flexDirection: {base: 'row-reverse', s: 'revert'},
                    justifyContent: 'space-between',
                    position: 'absolute',
                    bottom: 'spacing-16',
                    right: {base: 'spacing-16', s: 'spacing-16'},
                    gap: {base: 0, s: 'spacing-32'}
                },
                pagination: {display: 'none'},
                cockpitPagination: {display: 'flex'},
                buttonGroup: {
                    alignItems: 'center',
                    p: 'spacing-6',
                    borderRadius: 'round',
                    bg: 'primary.base.white',
                    textStyle: 'heading.small'
                },
                navButton: {variant: 'secondaryGhost', size: 'sm'}
            },
            configuratorQuickview: {
                scrollContainer: {
                    paddingY: '0',
                    borderRadius: 'none',
                    aspectRatio: '1/1'
                },
                slide: {
                    background: 'background.section.default'
                },
                controlsContainer: {
                    flexDirection: {base: 'row-reverse', s: 'revert'},
                    justifyContent: 'space-between',
                    position: 'absolute',
                    bottom: 'spacing-16',
                    right: {base: 'spacing-32', s: 'spacing-16'},
                    gap: {base: 0, s: 'spacing-32'}
                },
                pagination: {display: 'none'},
                cockpitPagination: {display: 'flex'},
                buttonGroup: {
                    alignItems: 'center',
                    p: 'spacing-6',
                    borderRadius: 'round',
                    bg: 'primary.base.white',
                    textStyle: 'heading.small'
                },
                navButton: {variant: 'secondaryGhost', size: 'sm'}
            },
            horizontalCards: {
                sliderContainer: {
                    '--items-per-slide': 1,
                    '--slide-gap': 'spacing-4',
                    display: {base: 'flex', s: 'contents'},
                    flexDirection: 'column-reverse',
                    position: 'relative',
                    width: '100%',
                    overflow: 'visible'
                },
                scrollContainer: {
                    display: {base: 'flex', s: 'contents'},
                    flexDirection: 'row',
                    flexWrap: 'nowrap',
                    gap: {base: 'spacing-8', s: '0'},
                    overflowX: {base: 'auto', s: 'visible'},
                    paddingInline: {base: 'spacing-24', s: '0'},
                    marginInline: {base: '-spacing-24', s: '0'},
                    alignItems: 'stretch'
                },
                controlsContainer: {
                    position: 'static',
                    display: {base: 'flex', s: 'none'},
                    flexDirection: 'row-reverse',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    paddingTop: 'spacing-32',
                    paddingInline: '0',
                    width: '100%',
                    gap: '0'
                },
                buttonGroup: {
                    display: 'flex',
                    gap: 'spacing-8'
                },
                navButton: {
                    variant: 'secondaryGhost',
                    bg: 'background.actions.ghost',
                    borderRadius: 'full'
                },
                slide: {
                    display: {base: 'flex', s: 'contents'},
                    flex: {base: '0 0 100%', s: 'unset'}
                }
            },
            productCarousel: {
                sliderContainer: {
                    '--items-per-slide': {base: 1.7, s: 4},
                    '--slide-gap': {base: 'sizes.2', s: 'sizes.4'}
                },
                scrollContainer: {
                    // span the viewport on mobile and increase the container width on desktop for the focus indicator to be fully visible
                    marginInline: {
                        base: 'calc(-1 * var(--container-total-inline-padding, var(--container-inline-padding, 0px)))',
                        s: '-spacing-4'
                    },
                    paddingInline: {
                        base: 'var(--container-total-inline-padding, var(--container-inline-padding, 0px))',
                        s: 'spacing-4'
                    },
                    scrollPaddingInline: {
                        base: 'var(--container-total-inline-padding, var(--container-inline-padding, 0px))',
                        s: 'spacing-4'
                    }
                },
                controlsContainer: {
                    flexDirection: 'row-reverse',
                    justifyContent: 'space-between'
                },
                cockpitPagination: {display: 'none'},
                buttonGroup: {
                    alignItems: 'center',
                    p: 'spacing-6',
                    borderRadius: 'round',
                    textStyle: 'heading.small'
                },
                navButton: {
                    variant: 'secondaryGhost'
                }
            }
        }
    },
    defaultVariants: {variant: 'default'}
})
